<!--
  Angular 15: *ngIf / *ngFor only, no @if blocks.
-->
<div class="dl-page">

  <div class="dl-page__header">
    <div class="dl-page__heading">
      <h1 class="dl-page__title">Plans and prices</h1>
      <p class="dl-page__subtitle">
        Prices are in EUR. A plan or module without both prices is not offered to anybody. A module set
        to "On payment" is sold at once but switches on only when the subscription is marked paid.
      </p>
    </div>
    <div class="dl-page__actions">
      <a class="dl-btn" routerLink="/subscriptions" data-testid="plans-subscriptions-link">
        <mat-icon>receipt_long</mat-icon>
        Subscriptions
      </a>
    </div>
  </div>

  <div class="dl-loading" *ngIf="loading">
    <div class="dl-spinner"></div>
    <p>Loading…</p>
  </div>

  <div class="dl-banner dl-banner--error" *ngIf="error">
    <mat-icon>error</mat-icon>
    <div class="dl-banner__body">{{ error }}</div>
    <div class="dl-banner__actions">
      <button type="button" class="dl-btn dl-btn--sm" (click)="load()">Try again</button>
    </div>
  </div>

  <ng-container *ngIf="!loading && !error">
    <h2 class="plans-section">Fixed plans</h2>
    <p class="plans-hint">
      One price for a set of modules. Each column is a plan. Tick what it includes, and set its limits where a module has
      one. Hover a module for what it does. Each plan has its own Save.
    </p>
    <!-- One column per plan, one row per module, so plans can be compared straight across. -->
    <div class="dl-card plans-compare" data-testid="plans-compare">
      <table class="plans-table">
        <thead>
          <tr>
            <th class="plans-table__label" scope="col">Plan</th>
            <th *ngFor="let plan of plans" scope="col" [attr.data-testid]="'plan-' + plan.code">
              <input class="dl-input" [(ngModel)]="plan.name" aria-label="Plan name"
                     [attr.data-testid]="'plan-name-' + plan.code">
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th class="plans-table__label" scope="row">Monthly €</th>
            <td *ngFor="let plan of plans">
              <input class="dl-input" type="number" min="0" step="0.01" [(ngModel)]="plan.monthlyPrice"
                     [attr.aria-label]="plan.name + ' monthly price'"
                     [attr.data-testid]="'plan-monthly-' + plan.code">
            </td>
          </tr>
          <tr>
            <th class="plans-table__label" scope="row">Yearly €</th>
            <td *ngFor="let plan of plans">
              <input class="dl-input" type="number" min="0" step="0.01" [(ngModel)]="plan.yearlyPrice"
                     [attr.aria-label]="plan.name + ' yearly price'"
                     [attr.data-testid]="'plan-yearly-' + plan.code">
            </td>
          </tr>
          <tr>
            <th class="plans-table__label" scope="row">On sale</th>
            <td *ngFor="let plan of plans">
              <input type="checkbox" [(ngModel)]="plan.active" [attr.aria-label]="plan.name + ' on sale'">
            </td>
          </tr>
          <tr *ngFor="let module of sellable" class="plans-table__module">
            <th class="plans-table__label" scope="row" [title]="descriptions[module.featureFlag!]">
              {{ labelOf(module) }}
              <small *ngIf="units[module.featureFlag!]">{{ units[module.featureFlag!] }}</small>
            </th>
            <td *ngFor="let plan of plans">
              <div class="plans-cell">
                <input type="checkbox" [ngModel]="includes(plan, module.featureFlag)"
                       (ngModelChange)="toggle(plan, module, $event)"
                       [attr.aria-label]="plan.name + ' includes ' + labelOf(module)"
                       [attr.data-testid]="'plan-' + plan.code + '-module-' + module.code">
                <input class="dl-input plans-limit" type="number" min="0"
                       *ngIf="includes(plan, module.featureFlag) && units[module.featureFlag!]"
                       [attr.aria-label]="plan.name + ' ' + labelOf(module) + ' limit'"
                       [ngModel]="limitOf(plan, module.featureFlag)"
                       (ngModelChange)="setLimit(plan, module.featureFlag, $event)">
              </div>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <th class="plans-table__label" scope="row"></th>
            <td *ngFor="let plan of plans">
              <button type="button" class="dl-btn dl-btn--primary" (click)="savePlan(plan)" [disabled]="saving === plan.code"
                      [attr.data-testid]="'plan-save-' + plan.code">Save</button>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>

    <h2 class="plans-section">Modules (flexible plan)</h2>
    <p class="plans-hint">
      A lab on a flexible plan pays Core plus each module it picks. "At once" means the module works as soon as it is
      bought. "On payment" keeps it off until the subscription is marked paid: use it for anything that costs us per use.
    </p>
    <div class="plans-module-grid">
    <div class="dl-card plans-card" *ngFor="let module of modules" [attr.data-testid]="'module-' + module.code">
      <p class="plans-module-title">
        <strong>{{ labelOf(module) }}</strong>
        <span>{{ module.featureFlag ? descriptions[module.featureFlag] : 'What a lab pays on a flexible plan before any module. Flexible plans are not offered until this has prices.' }}</span>
      </p>
      <div class="plans-row">
        <label class="plans-field plans-field--name">Name
          <input class="dl-input" [(ngModel)]="module.name">
        </label>
        <label class="plans-field">Monthly €
          <input class="dl-input" type="number" min="0" step="0.01" [(ngModel)]="module.monthlyPrice"
                 [attr.data-testid]="'module-monthly-' + module.code">
        </label>
        <label class="plans-field">Yearly €
          <input class="dl-input" type="number" min="0" step="0.01" [(ngModel)]="module.yearlyPrice"
                 [attr.data-testid]="'module-yearly-' + module.code">
        </label>
        <label class="plans-field" *ngIf="module.featureFlag && units[module.featureFlag]">Limit ({{ units[module.featureFlag] }})
          <input class="dl-input" type="number" min="0" [(ngModel)]="module.defaultLimit">
        </label>
        <label class="plans-field" *ngIf="module.featureFlag">Switches on
          <select class="dl-input" [(ngModel)]="module.activation" [attr.data-testid]="'module-activation-' + module.code">
            <option value="IMMEDIATE">At once</option>
            <option value="ON_PAYMENT">On payment</option>
          </select>
        </label>
        <label class="plans-check">
          <input type="checkbox" [(ngModel)]="module.active"> On sale
        </label>
        <button type="button" class="dl-btn dl-btn--primary" (click)="saveModule(module)" [disabled]="saving === module.code"
                [attr.data-testid]="'module-save-' + module.code">Save</button>
      </div>
    </div>
    </div>
  </ng-container>
</div>
